import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LogoutIcon from '@mui/icons-material/Logout';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import ModalGeneric from '../../Components/generic/ModalGeneric';
import LogoEmpresa from '../../Components/generic/LogoEmpresa';
import { useSessao } from '../../redux/sessao/sessaoHooks';
import { loginAction } from '../../redux/sessao/sessaoActions';
import apiAutenticacao from '../../api/apiAutenticacao';
import { getErroSenha, getRequisitosSenha } from '../../helper';

type Props = {
  open: boolean;
  onClose: () => void;
};

const ModalCadastrarSenha: React.FC<Props> = (props) => {
  const { open, onClose } = props;

  const [loading, setLoading] = useState(false);
  const [sessao, , logout] = useSessao();
  const dispatch = useDispatch();

  const [senha, setSenha] = useState('');
  const [verSenha, setVerSenha] = useState(false);

  const senhaTrimada = senha.trim();
  const erroSenhaAtual = senhaTrimada ? getErroSenha(senhaTrimada) : null;
  const requisitosSenha = getRequisitosSenha(senhaTrimada);
  const primeiroNome = sessao?.cliente?.nome?.split(' ')[0] ?? '';

  const cadastrarSenha = async () => {
    if (loading) return;

    try {
      if (!sessao?.clienteUsuario) return;

      const erroSenha = getErroSenha(senhaTrimada);

      if (erroSenha) {
        toast.error(erroSenha);
        return;
      }

      setLoading(true);

      const res = await apiAutenticacao.atualizarSenha(senhaTrimada);

      dispatch(loginAction(res));
      localStorage.setItem('sessao', JSON.stringify(res));

      toast.success(
        `Parabéns, ${primeiroNome}! Sua senha foi cadastrada com sucesso. Boa jornada!`
      );

      onClose();
    } catch (erro: any) {
      toast.error('Aconteceu algum erro ao cadastrar a senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalGeneric open={open} onClose={() => {}} hideCloseButton width="min(420px, 97vw)">
      <Box>
        <Box textAlign="center" mb={2}>
          <LogoEmpresa />

          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              letterSpacing: 0.2,
              color: 'text.primary'
            }}
          >
            Crie sua senha
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Acesso ao Portal de Relacionamento
          </Typography>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 2, lineHeight: 1.6 }}
        >
          Olá, {primeiroNome}! Defina uma nova senha e continue para acessar o portal. 🔐
        </Typography>

        <TextField
          fullWidth
          size="small"
          label="Senha"
          value={senha}
          onChange={(event) => setSenha(event.target.value.substring(0, 10))}
          type={verSenha ? 'text' : 'password'}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              cadastrarSenha();
            }
          }}
          disabled={loading}
          error={Boolean(erroSenhaAtual)}
          helperText={erroSenhaAtual ?? ''}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={verSenha ? 'Ocultar senha' : 'Mostrar senha'}
                  onClick={() => setVerSenha(!verSenha)}
                  edge="end"
                  disabled={loading}
                >
                  {verSenha ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            m: 0,
            mt: 0.5,
            mb: 2,
            p: 0,
            display: 'grid',
            gap: '2px'
          }}
        >
          {requisitosSenha.map((requisito) => (
            <Box
              component="li"
              key={requisito.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {requisito.atendido ? (
                <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
              ) : (
                <RadioButtonUncheckedIcon
                  sx={{
                    fontSize: 16,
                    color: 'text.disabled'
                  }}
                />
              )}

              <Typography
                variant="caption"
                color={requisito.atendido ? 'success.main' : 'text.secondary'}
              >
                {requisito.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Button
          fullWidth
          size="small"
          variant="contained"
          onClick={cadastrarSenha}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LockOpenIcon />}
          sx={{
            py: 1,
            textTransform: 'none',
            fontWeight: 700
          }}
        >
          {loading ? 'Cadastrando...' : 'Cadastrar senha'}
        </Button>

        <Button
          fullWidth
          size="small"
          variant="outlined"
          color="error"
          onClick={() => logout()}
          disabled={loading}
          startIcon={<LogoutIcon />}
          sx={{
            mt: 1,
            py: 1,
            textTransform: 'none'
          }}
        >
          Sair
        </Button>
      </Box>
    </ModalGeneric>
  );
};

export default ModalCadastrarSenha;
